import { useMemo } from "react";
import { Paper, Text, Stack, Table, Group, Badge } from "@mantine/core";
import { IconTrendingUp, IconTrendingDown } from "@tabler/icons-react";
import { useTableSort, SortableTh } from "../../hooks/Direction/useTableSort";
const ACCESSORS = {
  month: (m) => m.month,
  newLeads: (m) => m.newLeads ?? null,
  converted: (m) => m.converted ?? null,
  lost: (m) => m.lost ?? null,
  convRate: (m) => m.convRate ?? null,
};

/**
 * Evolución mensual de leads: nuevos, convertidos, perdidos y conversión.
 */
export default function MonthlyEvolutionChart({ monthlyEvolution }) {
  // La tendencia y el % de conversión se calculan ANTES de ordenar:
  // la flecha compara cada mes con el anterior en el tiempo, no con la fila
  // que quedó arriba después de ordenar.
  const rows = useMemo(() => {
    const list = monthlyEvolution || [];
    return list.map((m, idx) => {
      const prev = idx > 0 ? list[idx - 1] : null;
      const convRate =
        m.newLeads > 0 ? Math.round((m.converted / m.newLeads) * 1000) / 10 : 0;
      const trending =
        prev && prev.newLeads > 0
          ? m.newLeads > prev.newLeads
            ? "up"
            : m.newLeads < prev.newLeads
            ? "down"
            : "same"
          : null;

      return { ...m, convRate, trending };
    });
  }, [monthlyEvolution]);

  const { sorted, thProps } = useTableSort(rows, ACCESSORS);

  if (!monthlyEvolution || monthlyEvolution.length === 0) return null;

  const totals = monthlyEvolution.reduce(
    (acc, m) => ({
      newLeads: acc.newLeads + m.newLeads,
      converted: acc.converted + m.converted,
      lost: acc.lost + m.lost,
    }),
    { newLeads: 0, converted: 0, lost: 0 }
  );

  return (
    <Paper shadow="sm" radius="md" p="md">
      <Stack gap="sm">
        <Group justify="space-between">
          <Text fw={600} size="lg">
            Evolución Mensual
          </Text>
          <Group gap="xs">
            <Badge variant="light" color="blue" size="sm">
              {totals.newLeads} leads
            </Badge>
            <Badge variant="light" color="green" size="sm">
              {totals.converted} convertidos
            </Badge>
          </Group>
        </Group>

        <Table striped highlightOnHover withTableBorder withColumnBorders>
          <Table.Thead>
            <Table.Tr>
              <SortableTh field="month" {...thProps}>Mes</SortableTh>
              <SortableTh field="newLeads" align="center" {...thProps}>Nuevos</SortableTh>
              <SortableTh field="converted" align="center" {...thProps}>Convertidos</SortableTh>
              <SortableTh field="lost" align="center" {...thProps}>Perdidos</SortableTh>
              <SortableTh field="convRate" align="center" {...thProps}>Conversión</SortableTh>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sorted.map((m) => (
              <Table.Tr key={m.month} style={{ opacity: m.newLeads === 0 ? 0.4 : 1 }}>
                <Table.Td>
                  <Group gap={4} wrap="nowrap">
                    <Text size="sm" fw={500}>
                      {m.monthName}
                    </Text>
                    {m.trending === "up" && (
                      <IconTrendingUp size={14} color="var(--mantine-color-green-6)" />
                    )}
                    {m.trending === "down" && (
                      <IconTrendingDown size={14} color="var(--mantine-color-red-6)" />
                    )}
                  </Group>
                </Table.Td>
                <Table.Td style={{ textAlign: "center" }}>
                  <Text size="sm" fw={600}>{m.newLeads}</Text>
                </Table.Td>
                <Table.Td style={{ textAlign: "center" }}>
                  <Text size="sm" c="green" fw={600}>{m.converted}</Text>
                </Table.Td>
                <Table.Td style={{ textAlign: "center" }}>
                  <Text size="sm" c="red">{m.lost}</Text>
                </Table.Td>
                <Table.Td style={{ textAlign: "center" }}>
                  <Badge
                    variant="light"
                    color={m.convRate >= 10 ? "green" : m.convRate >= 5 ? "yellow" : "gray"}
                    size="sm"
                  >
                    {m.convRate}%
                  </Badge>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
          <Table.Tfoot>
            <Table.Tr>
              <Table.Td>
                <Text size="sm" fw={700}>TOTAL</Text>
              </Table.Td>
              <Table.Td style={{ textAlign: "center" }}>
                <Text size="sm" fw={700}>{totals.newLeads}</Text>
              </Table.Td>
              <Table.Td style={{ textAlign: "center" }}>
                <Text size="sm" fw={700} c="green">{totals.converted}</Text>
              </Table.Td>
              <Table.Td style={{ textAlign: "center" }}>
                <Text size="sm" fw={700} c="red">{totals.lost}</Text>
              </Table.Td>
              <Table.Td style={{ textAlign: "center" }}>
                <Badge variant="light" color="blue" size="sm">
                  {totals.newLeads > 0
                    ? Math.round((totals.converted / totals.newLeads) * 1000) / 10
                    : 0}%
                </Badge>
              </Table.Td>
            </Table.Tr>
          </Table.Tfoot>
        </Table>
      </Stack>
    </Paper>
  );
}